import React, { Fragment, FunctionComponent, PropsWithChildren, useState } from "react";

import { bemClassNames, invariant, TAB_INDEX_FOCUSABLE_TAB_SEQUENCE } from "@multiweb/reactUtils";
import { IconCode } from "@multiweb/types/contracts";
import { Role } from "@multiweb/uiShared/Role";
import { IconButton } from "../IconButton";
import { LocalizationMultiLoader } from "../LocalizationLoader";
import { LocalizationProvider } from "../LocalizationProvider";
import { CLOSE_LOC_KEY, CLOSE_WIZARD_LOC_KEY, NO_LOC_KEY, YES_LOC_KEY } from "../locKeys";
import { MessageWindow } from "../MessageWindow";
import { ModalWindow } from "../ModalWindow";
import { Text } from "../Text";
import { WizardControlPanel } from "../WizardControlPanel";

export type WizardWindowProps = PropsWithChildren<{
    /** Hodnota aktivního indexu */
    readonly activeIndex?: number;
    /** Příznak určující, zda jsou na stránce splněny všechny podmínky pro pokračování dále */
    readonly canProceed?: boolean;
    /** Počet stránek */
    readonly numberOfPages: number;
    /** Text pro tlačítko Zpět */
    readonly backButtonText: string;
    /** Text pro tlačítko Další */
    readonly nextButtonText: string;
    /** Text pro tlačítko Dokončit */
    readonly finishButtonText: string;
    /** Vlastní ikona progressu */
    readonly progressIcon?: IconCode;
    /** Šířka Wizard okna v px - defaultně je nastaveno na 800px */
    readonly width?: number;
    /** Výška Wizard okna v px - defaultně je nastaveno na 600px */
    readonly height?: number;
    /** Nastavení minimální výšky Wizard okna v px - defaultně je nastaveno na 400px */
    readonly minWidth?: number;
    /** Nastavení minimální šířky Wizard okna v px - defaultně je nastaveno na 400px */
    readonly minHeight?: number;
    /** Příznak určující, zda je možné s Wizard oknem pohybovat */
    readonly draggable?: boolean;
    /** Příznak určující, zda je možné Wizard okno zvětšovat/zmenšovat */
    readonly resizable?: boolean;
    /** Příznak určující, zda-li bude mít modal window průhledné pozadí nebo klasické MW (backdrop)  */
    readonly hasTransparentBackdrop?: boolean;
    /** Příznak určující, zda má Wizard okno tlačítko pro zavření */
    readonly hasCloseButton?: boolean;
    /** Příznak určující, zdali je Wizard window viditelné */
    readonly isHidden?: boolean;
    /** Callback funkce volána při kliku na tlačítko Další */
    readonly onNextButtonClick: () => void;
    /** Callback funkce volána při kliku na tlačítko Zpět */
    readonly onBackButtonClick: () => void;
    /** Callback funkce volána při kliku na tlačítko Dokončit */
    readonly onFinishButtonClick: () => void;
    /** Callback volaný při zmáčknutí tlačítka na zavření Wizardu. */
    readonly onWizardCloseButtonClick: () => void;
    /** Callback volaný při dokončení animace okna. */
    readonly onAnimationEnd: () => void;
}>;

const classNames = bemClassNames("wizard-window");
const wizardWindowClassName = classNames();
const closeButtonAreaCls = classNames("close-button-area");

export const WizardWindow: FunctionComponent<WizardWindowProps> = ({
    activeIndex = 0,
    canProceed = true,
    width = 800,
    height = 600,
    minWidth = 400,
    minHeight = 400,
    draggable = true,
    resizable = false,
    hasCloseButton = true,
    hasTransparentBackdrop,
    numberOfPages,
    isHidden,
    onNextButtonClick,
    onBackButtonClick,
    onFinishButtonClick,
    onWizardCloseButtonClick,
    onAnimationEnd,
    backButtonText,
    nextButtonText,
    finishButtonText,
    progressIcon,
    children,
}) => {
    const [isDisplayed, setIsDisplayed] = useState(true);
    const [isMessageWindowShown, setIsMessageWindowShown] = useState<boolean>(false);

    invariant(numberOfPages > 0, "You have to have at least one page.");
    invariant(activeIndex >= 0 && activeIndex <= numberOfPages - 1, "Active index is smaller than 0 or greater than number of pages.");

    const onCloseButtonClick = () => {
        setIsMessageWindowShown(true);
    };

    const onCloseMessageWindow = () => {
        setIsMessageWindowShown(false);
    };

    const handleCloseButtonClick = () => {
        setIsDisplayed(false);
        onWizardCloseButtonClick();
    };

    const handleFinishButtonClick = () => {
        setIsDisplayed(false);
        onFinishButtonClick();
    };

    return (
        <Fragment>
            <ModalWindow closeByOuterClick onOuterClick={onCloseButtonClick} onCloseChange={onAnimationEnd} {...{ hasTransparentBackdrop, draggable, resizable, isDisplayed, isHidden, defaultWidth: width, defaultHeight: height, minWidth, minHeight }}>
                <div className={wizardWindowClassName}>
                    {hasCloseButton &&
                        <LocalizationProvider locKey={CLOSE_LOC_KEY}>
                            {({ title }) => (
                                <div className={closeButtonAreaCls}>
                                    <IconButton role={Role.button} title={title} iconCode={IconCode.uiCross} tabIndex={TAB_INDEX_FOCUSABLE_TAB_SEQUENCE} onClick={onCloseButtonClick} />
                                </div>
                            )}
                        </LocalizationProvider>
                    }
                    {children}
                    <WizardControlPanel
                        activeIndex={activeIndex}
                        numberOfPages={numberOfPages}
                        canProceed={canProceed}
                        backButtonText={backButtonText}
                        nextButtonText={nextButtonText}
                        finishButtonText={finishButtonText}
                        onBackButtonClick={onBackButtonClick}
                        onNextButtonClick={onNextButtonClick}
                        onFinishButtonClick={handleFinishButtonClick}
                        progressIcon={progressIcon}
                    />
                </div>
            </ModalWindow>
            {isMessageWindowShown &&
                <LocalizationMultiLoader locKeys={[YES_LOC_KEY, NO_LOC_KEY, CLOSE_WIZARD_LOC_KEY]}>
                    <MessageWindow
                        primaryButtonLocKey={YES_LOC_KEY}
                        secondaryButtonLocKey={NO_LOC_KEY}
                        onPrimaryButtonClick={handleCloseButtonClick}
                        {...{ onCloseMessageWindow }}
                    >
                        <LocalizationProvider locKey={CLOSE_WIZARD_LOC_KEY}>
                            {({ text }) => <Text value={text} />}
                        </LocalizationProvider>
                    </MessageWindow>
                </LocalizationMultiLoader>}
        </Fragment>
    );
};
